import React, { useState } from 'react';
import { ShieldIcon, PlusIcon, XIcon, AlertCircleIcon } from 'lucide-react';
export function Guardrails() {
  const [newTopic, setNewTopic] = useState('');
  // Mock data for blocked topics
  const [blockedTopics, setBlockedTopics] = useState([{
    id: 1,
    name: 'Harmful instructions',
    description: 'Blocks prompts asking for harmful content or instructions',
    enabled: true
  }, {
    id: 2,
    name: 'Personal identifiable information',
    description: 'Prevents sharing of PII data',
    enabled: true
  }, {
    id: 3,
    name: 'Code execution',
    description: 'Blocks attempts to execute code on the server',
    enabled: true
  }, {
    id: 4,
    name: 'Jailbreaking',
    description: 'Blocks attempts to bypass system restrictions',
    enabled: true
  }, {
    id: 5,
    name: 'Illegal content',
    description: 'Prevents generation of illegal content',
    enabled: true
  }]);
  const handleAddTopic = () => {
    if (newTopic.trim()) {
      setBlockedTopics([...blockedTopics, {
        id: blockedTopics.length + 1,
        name: newTopic.trim(),
        description: 'User-defined topic',
        enabled: true
      }]);
      setNewTopic('');
    }
  };
  const handleRemoveTopic = id => {
    setBlockedTopics(blockedTopics.filter(topic => topic.id !== id));
  };
  const handleToggleTopic = id => {
    setBlockedTopics(blockedTopics.map(topic => topic.id === id ? {
      ...topic,
      enabled: !topic.enabled
    } : topic));
  };
  return <div className="space-y-6 text-white" data-id="element-1556">
      <div className="flex items-center justify-between" data-id="element-1557">
        <h2 className="text-xl font-bold text-white" data-id="element-1558">
          Guardrails Configuration
        </h2>
        <div className="bg-green-900 text-green-300 px-3 py-1 rounded-full text-sm font-medium flex items-center border border-green-800" data-id="element-1559">
          <ShieldIcon size={14} className="mr-1" data-id="element-1560" />
          Active
        </div>
      </div>
      <div className="bg-[#0F0F0F] rounded-lg shadow p-6 border border-[#222222]" data-id="element-1561">
        <h3 className="text-lg font-medium text-white mb-4" data-id="element-1562">
          Content Moderation
        </h3>
        <div className="mb-6" data-id="element-1563">
          <p className="text-sm text-gray-300 mb-4" data-id="element-1564">
            Configure topics that will be blocked by the content moderation
            system. When a user prompt matches any of these topics, the request
            will be rejected and logged for review.
          </p>
          <div className="flex items-center mb-6" data-id="element-1565">
            <input type="text" value={newTopic} onChange={e => setNewTopic(e.target.value)} placeholder="Add new topic to block" className="flex-1 px-4 py-2 bg-[#121212] border border-[#333333] rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white" data-id="element-1566" />
            <button onClick={handleAddTopic} className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 flex items-center" data-id="element-1567">
              <PlusIcon size={16} className="mr-1" data-id="element-1568" />
              Add Topic
            </button>
          </div>
          <div className="space-y-3" data-id="element-1569">
            {blockedTopics.map(topic => <div key={topic.id} className="flex items-center justify-between bg-[#121212] p-3 rounded-md border border-[#333333]" data-id="element-1570">
                <div className="flex-1" data-id="element-1571">
                  <h4 className="font-medium text-white" data-id="element-1572">{topic.name}</h4>
                  <p className="text-sm text-gray-400" data-id="element-1573">{topic.description}</p>
                </div>
                <div className="flex items-center space-x-3" data-id="element-1574">
                  <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-1575">
                    <input type="checkbox" id={`toggle-${topic.id}`} checked={topic.enabled} onChange={() => handleToggleTopic(topic.id)} className="sr-only" data-id="element-1576" />
                    <label htmlFor={`toggle-${topic.id}`} className={`block h-6 rounded-full cursor-pointer ${topic.enabled ? 'bg-green-600' : 'bg-gray-700'}`} data-id="element-1577">
                      <span className={`absolute left-0 top-0 h-6 w-6 rounded-full border border-gray-600 bg-[#0F0F0F] transition-transform transform ${topic.enabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-1578"></span>
                    </label>
                  </div>
                  <button onClick={() => handleRemoveTopic(topic.id)} className="p-1 text-gray-400 hover:text-red-400" data-id="element-1579">
                    <XIcon size={16} data-id="element-1580" />
                  </button>
                </div>
              </div>)}
          </div>
        </div>
        <div className="border-t border-[#222222] pt-4" data-id="element-1581">
          <div className="bg-yellow-900/30 border border-yellow-800 rounded-md p-3 flex items-start" data-id="element-1582">
            <AlertCircleIcon size={18} className="text-yellow-400 mr-2 mt-0.5" data-id="element-1583" />
            <div data-id="element-1584">
              <h4 className="font-medium text-yellow-300 mb-1" data-id="element-1585">
                Important Note
              </h4>
              <p className="text-sm text-yellow-200" data-id="element-1586">
                Guardrails provide a basic level of content moderation but are
                not 100% effective. Regular review of logs and continuous
                improvement of blocked topics is recommended.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#0F0F0F] rounded-lg shadow p-6 border border-[#222222]" data-id="element-1587">
        <h3 className="text-lg font-medium text-white mb-4" data-id="element-1588">
          Moderation Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-1589">
          <div className="bg-[#121212] rounded-md p-4 border border-[#333333]" data-id="element-1590">
            <h4 className="text-sm text-gray-400 mb-1" data-id="element-1591">Blocked Today</h4>
            <p className="text-2xl font-semibold text-white" data-id="element-1592">143</p>
          </div>
          <div className="bg-[#121212] rounded-md p-4 border border-[#333333]" data-id="element-1593">
            <h4 className="text-sm text-gray-400 mb-1" data-id="element-1594">Blocked This Week</h4>
            <p className="text-2xl font-semibold text-white" data-id="element-1595">892</p>
          </div>
          <div className="bg-[#121212] rounded-md p-4 border border-[#333333]" data-id="element-1596">
            <h4 className="text-sm text-gray-400 mb-1" data-id="element-1597">Block Rate</h4>
            <p className="text-2xl font-semibold text-white" data-id="element-1598">2.4%</p>
          </div>
        </div>
        <div className="text-right" data-id="element-1599">
          <button className="text-sm text-blue-400 hover:text-blue-300 font-medium" data-id="element-1600">
            View Detailed Reports →
          </button>
        </div>
      </div>
    </div>;
}